import { useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, RotateCcw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Loader } from "@/components/shared/loader";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  useDownloadBackup,
  useRestoreBackup,
} from "@/features/backups/hooks/useBackupsMutations";
import { formatOptionalArabicDateTime, formatBytes } from "@/lib/formatters";
import {
  BACKUP_TYPE_LABELS,
  BACKUP_STATUS_LABELS,
  type Backup,
} from "@/types/backup";

function statusVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  if (status === "completed") return "default";
  if (status === "failed") return "destructive";
  return "secondary";
}

interface RowActionsProps {
  backup: Backup;
  canRestore: boolean;
}

function BackupRowActions({ backup, canRestore }: RowActionsProps) {
  const download = useDownloadBackup();
  const restore = useRestoreBackup();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isCompleted = backup.status === "completed";

  const runRestore = async () => {
    try {
      await restore.mutateAsync(backup.id);
      setConfirmOpen(false);
    } catch {
      /* handled by the mutation's onError toast */
    }
  };

  return (
    <div className="flex items-center justify-end gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={!isCompleted || download.isPending}
        onClick={() => download.mutate(backup.id)}
      >
        <Download className="ml-1 size-3.5" />
        تنزيل
        {download.isPending && <Loader size="sm" className="mr-2" />}
      </Button>

      {canRestore && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(o) => setConfirmOpen(o)}
          variant="destructive"
          title="تأكيد الاستعادة"
          description="سيتم استبدال بيانات النظام الحالية ببيانات هذه النسخة الاحتياطية. هذا إجراء لا يمكن التراجع عنه. هل تريد المتابعة؟"
          actionLabel="استعادة"
          isLoading={restore.isPending}
          onConfirm={runRestore}
          trigger={
            <Button
              variant="destructive"
              size="sm"
              disabled={!isCompleted || restore.isPending}
            >
              <RotateCcw className="ml-1 size-3.5" />
              استعادة
            </Button>
          }
        />
      )}
    </div>
  );
}

interface BackupsTableProps {
  backups: Backup[];
  canRestore: boolean;
}

export function BackupsTable({ backups, canRestore }: BackupsTableProps) {
  const columns: ColumnDef<Backup>[] = [
    {
      accessorKey: "type",
      header: "النوع",
      cell: ({ row }) =>
        BACKUP_TYPE_LABELS[
          row.original.type as keyof typeof BACKUP_TYPE_LABELS
        ] ?? row.original.type,
    },
    {
      accessorKey: "status",
      header: "الحالة",
      cell: ({ row }) => (
        <div className="flex flex-col items-start gap-1">
          <Badge variant={statusVariant(row.original.status)}>
            {BACKUP_STATUS_LABELS[row.original.status] ?? row.original.status}
          </Badge>
          {row.original.status === "failed" && row.original.notes && (
            <span
              dir="ltr"
              title={row.original.notes}
              className="max-w-xs truncate text-xs text-destructive"
            >
              {row.original.notes}
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "sizeBytes",
      header: "الحجم",
      cell: ({ row }) => formatBytes(row.original.sizeBytes),
    },
    {
      accessorKey: "createdAt",
      header: "تاريخ الإنشاء",
      cell: ({ row }) => formatOptionalArabicDateTime(row.original.createdAt),
    },
    {
      accessorKey: "completedAt",
      header: "تاريخ الاكتمال",
      cell: ({ row }) => formatOptionalArabicDateTime(row.original.completedAt),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <BackupRowActions backup={row.original} canRestore={canRestore} />
      ),
    },
  ];

  return <DataTable columns={columns} data={backups} />;
}
